import classNames from 'classnames';
import { isNumber } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  APPLY_REGISTER_CLASS_ID,
  APPLY_REGISTER_ID,
  DATE_RANGE_SEARCH_PARAM_KEY,
  ENTITY_ID_SEARCH_PARAM_KEY,
  REGISTER_ID_SEARCH_PARAM_KEY,
  SUBSCRIBE_FROM_PARAM_KEY,
} from '@/modules/common/constants/search-params.ts';
import { useGetApplyRegisterName } from '@/modules/common/hooks/useGetApplyRegisterName.ts';
import { queryClient } from '@/modules/common/query.ts';
import { CURRENT_ENTITY_CREATING_KEY } from '@/modules/entity/queries/entity.tsx';
import { entityPaths } from '@/modules/entity/routes.tsx';
import { useMyActivitiesListQuery } from '@/modules/investor-dashboard/queries/activity.tsx';
import { useGetRegisterClassItem } from '@/modules/investor-dashboard/queries/registers.tsx';
import { useCreateShareholderMutation } from '@/modules/investor-dashboard/queries/shareholders.tsx';
import { useCurrentEntityShareholdersQuery } from '@/modules/investor-dashboard/queries/transactions.tsx';
import { investorDashboardPaths } from '@/modules/investor-dashboard/routes.tsx';
import { Button, Spinner, TooltipNew, getSelectValueByEnum } from 'ui';
import { AlertOrangeIcon, AlertWhiteIcon } from 'ui/icons';
import { fixNameEnum } from 'ui/utils';
import { ActivitiesType, EntityStatus, EntityWithDocumentResponse } from 'api';

export enum EntityTypeFixed {
  Individual = 'individual',
  Company = 'company',
  Trust = 'trust',
  Partnership = 'partnership',
  'Association & Not for profits' = 'association',
  'Self Managed Superannuation Fund' = 'smsf',
}

const entityStatusMessages: Record<EntityStatus, string> = {
  [EntityStatus.EntityCreation]:
    'This entity is being created. Please complete all required information before proceeding.',

  [EntityStatus.EntityAwaitingApproval]:
    'This entity is awaiting approval. You’ll be notified once it’s reviewed.',

  [EntityStatus.EntityApproved]:
    'This entity has been approved. You can now proceed with the next steps.',

  [EntityStatus.EntityUnsuccessfull]:
    'This entity was unsuccessful. We can’t perform a Follow Up because the Bron ID didn’t return the requested actions field.',

  [EntityStatus.PermanentlyRejected]:
    'This entity was permanently rejected. Please create a new one to continue.',
};

export interface EntityListItemProps {
  entity: EntityWithDocumentResponse;
}

export const EntityListItem = ({ entity }: EntityListItemProps) => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [isNestedTooltipHovered, setIsNestedTooltipHovered] = useState(false);
  const apply_register_id = params.get(APPLY_REGISTER_ID);
  const apply_register_class_id = params.get(APPLY_REGISTER_CLASS_ID);

  const { status, type, entity_name, entity_application_rejection_reason } = entity;

  const { data: entityShareholdersResponse, isFetching: isFetchingEntityShareholders } =
    useCurrentEntityShareholdersQuery(Number(entity.id), !entity.id || +entity.id < 1);
  const register_id = useMemo(
    () => (entityShareholdersResponse?.shareholders || [])?.[0]?.register_id || 'all',
    [entityShareholdersResponse?.shareholders],
  );

  const register_ids = useMemo(
    () =>
      (entityShareholdersResponse?.shareholders || [])?.map(shareholder => shareholder.register_id),
    [entityShareholdersResponse?.shareholders],
  );

  const { mutateAsync: createShareholder, isLoading: isLoadingCreateShareholder } =
    useCreateShareholderMutation({
      onSuccess: data => {
        if (apply_register_class_id) {
          navigate(
            {
              pathname: entityPaths.static.subscribe + `/${data.shareholder.entity_id}`,
              search: params.toString(),
            },
            {
              replace: false,
            },
          );
        }
      },
    });

  const handleCreateShareholder = useCallback(
    async (entityId: number) => {
      await createShareholder({ register_id: Number(apply_register_id), entity_id: entityId });
    },
    [apply_register_id, createShareholder],
  );

  const { data: dataAppliedClassTransactions } = useMyActivitiesListQuery(
    {
      page: 1,
      page_size: 1,
      type: ActivitiesType.Subscription,
      register_class_id: Number(apply_register_class_id),
    },
    Number(apply_register_id),
    !apply_register_id ||
      !apply_register_class_id ||
      !isNumber(+apply_register_class_id) ||
      !entity.id,
    Number(entity.id),
  );

  // const { data: dataTransactions } = useMyActivitiesListQuery(
  //   {
  //     page: 1,
  //     page_size: 1,
  //     type: ActivitiesType.Subscription,
  //   },
  //   Number(apply_register_id),
  //   !entity.id,
  //   Number(entity.id),
  // );

  const {
    isError: isErrorRegister,
    isLoading: isLoadingRegister,
    name: joinRegisterName,
  } = useGetApplyRegisterName();
  const {
    isError: isErrorRegisterClass,
    isLoading: isLoadingRegisterClass,
    data: dataClassSubscribe,
  } = useGetRegisterClassItem(
    Number(apply_register_class_id),
    !apply_register_class_id || isNaN(+apply_register_class_id),
  );

  // const isSubscribedToAppliedClass = useMemo(() => {
  //   if (
  //     isErrorRegister ||
  //     isLoadingRegister ||
  //     isErrorRegisterClass ||
  //     isLoadingRegisterClass ||
  //     !apply_register_id ||
  //     isNaN(+apply_register_id) ||
  //     !apply_register_class_id ||
  //     isNaN(+apply_register_class_id)
  //   )
  //     return true;
  //
  //   return (dataAppliedClassTransactions?.pages?.[0]?.data?.length ?? 1) > 0; //default 1 because it's off Subscribe
  // }, [
  //   apply_register_class_id,
  //   apply_register_id,
  //   dataAppliedClassTransactions?.pages,
  //   isErrorRegister,
  //   isErrorRegisterClass,
  //   isLoadingRegister,
  //   isLoadingRegisterClass,
  // ]);

  const isShareholderExist = useMemo(() => {
    if (
      isErrorRegister ||
      isLoadingRegister ||
      isErrorRegisterClass ||
      isLoadingRegisterClass ||
      !apply_register_id ||
      isNaN(+apply_register_id)
    )
      return true;

    return !!(entityShareholdersResponse?.shareholders || []).find(
      item => item.register_id === Number(apply_register_id),
    );
  }, [
    apply_register_id,
    entityShareholdersResponse?.shareholders,
    isErrorRegister,
    isErrorRegisterClass,
    isLoadingRegister,
    isLoadingRegisterClass,
  ]);

  const handleSubscribe = useCallback(async () => {
    if (
      // !isSubscribedToAppliedClass &&
      !(
        entity.status === EntityStatus.EntityAwaitingApproval ||
        entity.status === EntityStatus.EntityApproved ||
        entity.status === EntityStatus.EntityCreation
      )
    )
      return;

    params.set(SUBSCRIBE_FROM_PARAM_KEY, 'list');
    setParams(params, { replace: false });
    try {
      if (entity.id && !isShareholderExist) {
        await handleCreateShareholder(entity.id);
      }
    } catch (e: any) {
      console.log('Error during creating Shareholder: ', e?.response?.data?.error_message ?? '');
    } finally {
      navigate(
        {
          pathname: entityPaths.static.subscribe + `/${entity.id}`,
          search: params.toString(),
        },
        {
          replace: false,
        },
      );
    }
  }, [entity, handleCreateShareholder, isShareholderExist, navigate, params, setParams]);

  const handleGoDashboard = useCallback(() => {
    if (
      !(
        entity.status === EntityStatus.EntityCreation ||
        entity.status === EntityStatus.EntityAwaitingApproval ||
        entity.status === EntityStatus.EntityApproved
      )
    )
      return;

    const search = createSearchParams({
      [ENTITY_ID_SEARCH_PARAM_KEY]: String(entity.id),
      [REGISTER_ID_SEARCH_PARAM_KEY]: String(register_id),
      [DATE_RANGE_SEARCH_PARAM_KEY]: 'all',
      ...(params.get('entity_id') === String(entity.id)
        ? Object.fromEntries(params.entries())
        : {}),
      [APPLY_REGISTER_ID]: String(apply_register_id ?? ''),
      [APPLY_REGISTER_CLASS_ID]: String(apply_register_class_id ?? ''),
    });
    if (!apply_register_id) {
      search.delete(APPLY_REGISTER_ID);
    }
    if (!apply_register_class_id) {
      search.delete(APPLY_REGISTER_CLASS_ID);
    }
    navigate(
      {
        pathname: investorDashboardPaths.static.dashboard,
        search: search.toString(),
      },
      {
        replace: false,
      },
    );
  }, [apply_register_class_id, apply_register_id, entity, navigate, params, register_id]);

  const canClickFollowUp = useMemo(() => {
    const status = entity.status === EntityStatus.EntityUnsuccessfull;
    return status && !!entity?.bron_id_requested_actions && !entity?.is_followup_sent;
  }, [entity?.bron_id_requested_actions, entity.status, entity?.is_followup_sent]);

  const handleClickOnFollowUp = useCallback(async () => {
    if (canClickFollowUp) {
      navigate(
        {
          pathname: entityPaths.static.followUp + `/${entity.id}`,
          search: params.toString(),
        },
        {
          replace: false,
        },
      );
    }
  }, [canClickFollowUp, entity.id, navigate, params]);

  const handleClickOnEntity = useCallback(async () => {
    if (entity.type === null) {
      navigate(
        {
          pathname: entityPaths.static.abnLookupEntity,
          search: params.toString(),
        },
        { state: { entityId: entity.id } },
      );
      return;
    }
    if (
      (entity.status === EntityStatus.EntityCreation ||
        entity.status === EntityStatus.EntityAwaitingApproval ||
        entity.status === EntityStatus.EntityApproved) &&
      !isNaN(+register_id)
    ) {
      handleGoDashboard();
    }
  }, [entity, handleGoDashboard, navigate, params, register_id]);

  const { mutateAsync: mutateAsyncCreateShareholder } = useCreateShareholderMutation({
    onSuccess: handleGoDashboard,
  });

  const handleClickCreationEntity = useCallback(() => {
    queryClient.setQueryData([CURRENT_ENTITY_CREATING_KEY], { entity });
    params.forEach((_, key) => {
      if (key !== APPLY_REGISTER_ID && key !== APPLY_REGISTER_CLASS_ID) {
        params.delete(key);
      }
    });
    setParams(params, { replace: false });

    if (entity.type) {
      navigate(
        {
          pathname: entityPaths.static.updateEntity + `/${entity.id}`,
          search: params.toString(),
        },
        {
          replace: false,
        },
      );
    } else {
      navigate(
        // case when registered through transfer
        {
          pathname: entityPaths.static.abnLookupEntity,
          search: params.toString(),
        },
        { state: { entityId: entity.id }, replace: false },
      );
    }
  }, [entity, navigate, params]);

  const canClickDashboard = useMemo(() => {
    const status =
      entity.status === EntityStatus.EntityCreation ||
      entity.status === EntityStatus.EntityAwaitingApproval ||
      entity.status === EntityStatus.EntityApproved;
    return !isNaN(register_ids?.[0]) && status;
  }, [entity.status, register_ids]);

  const canJoinToRegister = useMemo(() => {
    const params =
      apply_register_id &&
      !isNaN(+apply_register_id) &&
      !isFetchingEntityShareholders &&
      !register_ids.includes(+apply_register_id);

    const status =
      entity.status === EntityStatus.EntityAwaitingApproval ||
      entity.status === EntityStatus.EntityApproved ||
      entity.status === EntityStatus.EntityCreation;
    return params && status;
  }, [apply_register_id, entity.status, isFetchingEntityShareholders, register_ids]);

  const canClickSubscribe = useMemo(() => {
    const params =
      !!apply_register_id &&
      !isNaN(+apply_register_id) &&
      !!apply_register_class_id &&
      !isNaN(+apply_register_class_id);
    // const joinRegister = !!apply_register_id && register_ids.includes(+apply_register_id);
    const status =
      entity.status === EntityStatus.EntityAwaitingApproval ||
      entity.status === EntityStatus.EntityApproved ||
      entity.status === EntityStatus.EntityCreation;
    return params && status; //&& joinRegister;
  }, [apply_register_class_id, apply_register_id, entity.status, register_ids]);

  const subscribedStatus = useMemo(() => {
    if (!canClickSubscribe) {
      return '';
    }
    const className = dataClassSubscribe?.register_class?.class_name ?? '';
    const status = dataAppliedClassTransactions?.pages?.[0]?.data?.[0]?.status;

    if (!status) {
      return `You are not subscribed to the class ${className}`;
    }

    return `Your subscription to the class ${className} is ${fixNameEnum(status)?.toLowerCase()}`;
  }, [
    canClickSubscribe,
    dataAppliedClassTransactions?.pages,
    dataClassSubscribe?.register_class?.class_name,
  ]);

  const handleShowToastNotRegister = useCallback(() => {
    if (isFetchingEntityShareholders) return;

    if (
      (entity.status === EntityStatus.EntityUnsuccessfull && !entity?.bron_id_requested_actions) ||
      entity.status === EntityStatus.PermanentlyRejected
    ) {
      toast(
        t => (
          <span className="flex flex-col justify-center items-center gap-2">
            <span>
              <img src={AlertOrangeIcon} className="w-8 h-8" alt="alert" />
            </span>
            {entityStatusMessages[entity.status as EntityStatus]}
            <Button variant="plain" onClick={() => toast.dismiss(t.id)}>
              Dismiss
            </Button>
          </span>
        ),
        { duration: 10000, id: 'not_register' },
      );
      return;
    }

    // if (
    //   !(
    //     entity.status === EntityStatus.EntityCreation ||
    //     entity.status === EntityStatus.EntityAwaitingApproval ||
    //     entity.status === EntityStatus.EntityApproved
    //   )
    // ) {
    //   toast(
    //     t => (
    //       <span className="flex flex-col justify-center items-center gap-2">
    //         <span>
    //           <img src={AlertOrangeIcon} className="w-8 h-8" alt="alert" />
    //         </span>
    //         {entityStatusMessages[entity.status]}
    //         <Button variant="plain" onClick={() => toast.dismiss(t.id)}>
    //           Dismiss
    //         </Button>
    //       </span>
    //     ),
    //     { duration: 10000, id: 'not_register' },
    //   );
    //   return;
    // }

    if (apply_register_id && !isNaN(+apply_register_id) && canJoinToRegister) {
      mutateAsyncCreateShareholder({
        entity_id: entity.id,
        register_id: +apply_register_id,
      });
      return;
    }

    toast(
      t => (
        <span className="flex flex-col justify-center items-center gap-2 text-sm">
          <span>
            <img src={AlertOrangeIcon} className="w-8 h-8" alt="alert" />
          </span>
          This entity does not participate in any registry, please ask your issuer to send you an
          invite link and then go by this link selecting this entity to join this registry
          <Button variant="plain" onClick={() => toast.dismiss(t.id)}>
            Dismiss
          </Button>
        </span>
      ),
      { duration: 10000, id: 'not_register' },
    );
  }, [
    apply_register_id,
    canJoinToRegister,
    entity?.bron_id_requested_actions,
    entity.id,
    entity.status,
    isFetchingEntityShareholders,
    mutateAsyncCreateShareholder,
  ]);

  const isSubscribePending = useMemo(() => {
    const status = dataAppliedClassTransactions?.pages?.[0]?.data?.[0]?.status;
    return 'awaiting_payment' === status || 'pending' === status;
  }, [dataAppliedClassTransactions?.pages]);

  const handleClick = useMemo(() => {
    if (canClickFollowUp) {
      return handleClickOnFollowUp;
    }
    if (canClickSubscribe && !isSubscribePending) {
      return handleSubscribe;
    }
    if (canJoinToRegister) {
      return handleShowToastNotRegister;
    }
    if (canClickDashboard) {
      return handleClickOnEntity;
    }
    return handleShowToastNotRegister;
  }, [
    canJoinToRegister,
    canClickFollowUp,
    status,
    canClickSubscribe,
    isSubscribePending,
    canClickDashboard,
    handleShowToastNotRegister,
    handleClickOnFollowUp,
    handleClickCreationEntity,
    handleSubscribe,
    handleClickOnEntity,
  ]);

  const titleText = useMemo(() => {
    if (isFetchingEntityShareholders) {
      return 'Loading...';
    }

    if (canClickFollowUp) {
      return 'Click to fulfill the follow up information request.';
    }
    if (canClickSubscribe && !isSubscribePending) {
      return 'Click to subscribe';
    }
    if (canJoinToRegister) {
      return `Click to join the invite register ${joinRegisterName ?? ''}`;
    }
    if (
      !canClickDashboard &&
      (entity.status === EntityStatus.EntityCreation ||
        entity.status === EntityStatus.EntityAwaitingApproval ||
        entity.status === EntityStatus.EntityApproved)
    ) {
      return `You can't go to the dashboard or subscribe without an invitation to the registry`;
    }
    return '';
  }, [
    isFetchingEntityShareholders,
    canJoinToRegister,
    canClickFollowUp,
    status,
    canClickSubscribe,
    isSubscribePending,
    canClickDashboard,
    entity.status,
    joinRegisterName,
  ]);

  const isEntityNameTooltipEnabled = !!entity_name && entity_name.length > 23;

  const handleNestedTooltipMouseEnter = useCallback(() => {
    setIsNestedTooltipHovered(true);
  }, []);

  const handleNestedTooltipMouseLeave = useCallback(() => {
    setIsNestedTooltipHovered(false);
  }, []);

  return (
    <TooltipNew
      text={titleText}
      className="text-wrap text-ellipsis w-full"
      disabled={!titleText || isNestedTooltipHovered}
    >
      <div
        onClick={handleClick}
        className={classNames(
          'w-full flex sm:flex-row flex-col justify-between sm:items-center items-start gap-2',
          'rounded-lg border border-gray-900 border-opacity-16 p-4 relative',
          {
            'cursor-wait': isFetchingEntityShareholders,
            'hover:bg-purple-50 cursor-pointer': !isFetchingEntityShareholders && canClickFollowUp,
            'cursor-pointer hover:bg-gray-100':
              !isFetchingEntityShareholders &&
              canClickDashboard &&
              !canJoinToRegister &&
              !(canClickSubscribe && !isSubscribePending) &&
              !canClickFollowUp,
            'cursor-pointer hover:bg-red-50':
              !isFetchingEntityShareholders &&
              !canClickDashboard &&
              !canJoinToRegister &&
              !canClickFollowUp,
            'cursor-pointer hover:bg-blue-50':
              !isFetchingEntityShareholders && canJoinToRegister && !canClickFollowUp,
            'cursor-pointer hover:bg-cyan-50':
              !isFetchingEntityShareholders &&
              canClickSubscribe &&
              !isSubscribePending &&
              !canClickFollowUp,
          },
        )}
      >
        <div className="flex flex-col gap-1">
          <div className="flex sm:flex-row flex-col gap-2">
            <div
              onMouseEnter={isEntityNameTooltipEnabled ? handleNestedTooltipMouseEnter : undefined}
              onMouseLeave={isEntityNameTooltipEnabled ? handleNestedTooltipMouseLeave : undefined}
            >
              <TooltipNew
                text={entity_name ? entity_name : 'Entity name is unset'}
                classNameTooltip="text-wrap w-full"
                className="sm:max-w-[230px] overflow-x-hidden text-ellipsis text-nowrap"
                variant={'light'}
                disabled={!isEntityNameTooltipEnabled}
              >
                <span
                  className={classNames(
                    'text-base capitalize w-fit cursor-pointer hover:underline',
                  )}
                >
                  {entity_name ? entity_name : 'Entity name is unset'}
                </span>
              </TooltipNew>
            </div>
            <div
              className={classNames({
                'w-[160px]': status !== EntityStatus.PermanentlyRejected,
                'w-[180px]': status === EntityStatus.PermanentlyRejected,
              })}
            >
              <div
                className={classNames(
                  'text-[12px] leading-[20px] text-white',
                  'rounded-[1000px] px-2 py-[2px] capitalize h-6 w-fit',
                  {
                    'bg-success-green': status === EntityStatus.EntityApproved,
                    'bg-blue-700': status === EntityStatus.EntityAwaitingApproval,
                    'bg-tmnt-purple': status === EntityStatus.EntityUnsuccessfull,
                    'bg-warning-orange': status === EntityStatus.EntityCreation,
                    'bg-error-red': status === EntityStatus.PermanentlyRejected,
                  },
                )}
              >
                <span className="flex flex-row space-y-5 justify-center items-center">
                  {status === EntityStatus.EntityCreation && 'Entity in Creation'}
                  {status === EntityStatus.EntityUnsuccessfull && 'Unsuccessful'}
                  {status !== EntityStatus.EntityCreation &&
                    status !== EntityStatus.EntityUnsuccessfull &&
                    fixNameEnum(status)}
                  {[EntityStatus.EntityUnsuccessfull, EntityStatus.PermanentlyRejected].includes(
                    status as EntityStatus,
                  ) &&
                    entity_application_rejection_reason && (
                      <span
                        onMouseEnter={handleNestedTooltipMouseEnter}
                        onMouseLeave={handleNestedTooltipMouseLeave}
                      >
                        <TooltipNew
                          className="normal-case"
                          positionStrategy="fixed"
                          classNameTooltip="w-full min-w-[600px]"
                          text={entity_application_rejection_reason}
                        >
                          <img
                            src={AlertWhiteIcon}
                            className="text-sm ml-1 max-w-[16px]"
                            alt="info"
                          />
                        </TooltipNew>
                      </span>
                    )}
                </span>
              </div>
            </div>
          </div>
          <p className={`text-[14px] leading-[22px] text-gray-600 ${type ? 'capitalize' : ''}`}>
            {type
              ? getSelectValueByEnum<EntityTypeFixed>(type, EntityTypeFixed).label
              : 'Entity type has not been set'}
          </p>
          {subscribedStatus && (
            <p className={`text-xs leading-5 text-gray-700`}>{subscribedStatus}</p>
          )}
        </div>

        <div className="flex sm:flex-col flex-row gap-2">
          {canClickSubscribe && (
            <div className="min-w-[135px]" onClick={e => e.stopPropagation()}>
              <Button
                variant="plain"
                className="px-2 py-1"
                onClick={handleSubscribe}
                loading={isLoadingCreateShareholder}
              >
                Subscribe
              </Button>
            </div>
          )}
          {status === EntityStatus.EntityCreation && (
            <div className="min-w-[135px]" onClick={e => e.stopPropagation()}>
              <Button variant="plain" className="px-2 py-1" onClick={handleClickCreationEntity}>
                Continue KYC
              </Button>
            </div>
          )}
          {canClickFollowUp && (
            <div className="min-w-[135px]" onClick={e => e.stopPropagation()}>
              <Button variant="plain" className="px-2 py-1" onClick={handleClickOnFollowUp}>
                Follow Up
              </Button>
            </div>
          )}
          {entity?.is_followup_sent === true && (
            <div className="min-w-[135px]" onClick={e => e.stopPropagation()}>
              <Button
                variant="plain"
                className="px-2 py-1"
                onClick={handleClickOnFollowUp}
                disabled={true}
              >
                <Spinner size="sm" /> Follow Up is checking...
              </Button>
            </div>
          )}
        </div>
      </div>
    </TooltipNew>
  );
};
